import { ValidationChecks } from './validation-checks.enum';
import { ValidatorInput } from './validation-descriptor.entity';
import { ValidationError } from './validation.error';

interface POSIX {
    class: string;
    equivalent: string;
}

/**
 * The real validator.
 */
export class Validator {

    private static readonly today: Date = new Date();

    private static readonly maxFlag = 1 << 30;
    static readonly DATE_REGEX = /^(?:(?:(?:0[1-9]|1\d|2[0-8])\.(?:0[1-9]|1[0-2])|(?:29|30)\.(?:0[13-9]|1[0-2])|31\.(?:0[13578]|1[02]))\.[1-9]\d{3}|29\.02\.(?:[1-9]\d(?:0[48]|[2468][048]|[13579][26])|(?:[2468][048]|[13579][26])00))$/;

    static readonly MSG_ON_REQUIRED_BUT_EMPTY = `Eine Eingabe für '$_label' ist erforderlich.`;
    static readonly MSG_ON_REGEX_FAIL = `Der Wert '$_value' ist keine korrekte Eingabe für '$_label'.`;
    static readonly MSG_ON_NONE_SELECTED = `Bitte wählen Sie eine Option für '$_label'.`;
    static readonly MSG_WRONG_DATE_FORMAT = 'Geben Sie bitte ein korrektes Datum ein';
    static readonly MSG_DATE_IN_PAST = 'Das Datum muss in der Zukunft liegen.';
    static readonly MSG_DATE_IN_FUTURE = 'Das Datum darf nicht in der Zukunft liegen.';
    static readonly MSG_DATE_TO_FAR = 'Das Datum wird nicht akzeptiert.';
    static readonly MSG_VORWAHL_RUFNUMMER = `Das Feld 'Rufnummer' und das Feld 'Vorwahl' müssen gefüllt sein.`;
    static readonly MSG_WRONG_PERCENT = 'Geben Sie einen Wert zwischen 1 und 100 ein.';

    private static POSIX: POSIX[] = [
        {
            class: '[[:alnum:]]',
            equivalent: 'a-zA-Z0-9',
        },
        {
            class: '[[:alpha:]]',
            equivalent: 'a-zA-Z',
        },
        {
            class: '[[:blank:]]',
            equivalent: ' \\t',
        },
        {
            class: '[[:cntrl:]]',
            equivalent: '\\x00-\\x1F\\x7F',
        },
        {
            class: '[[:digit:]]',
            equivalent: '0-9',
        },
        {
            class: '[[:graph:]]',
            equivalent: '\\x21-\\x7E',
        },
        {
            class: '[[:lower:]]',
            equivalent: 'a-z',
        },
        {
            class: '[[:print:]]',
            equivalent: '\\x20-\\x7E',
        },
        {
            class: '[[:punct:]]',
            equivalent: '\\-!"#$€§%&\'()\\*+,.\\/:;<=>?@\\[\\\\\\]^_`{|}~',
        },
        {
            class: '[[:space:]]',
            equivalent: ' \\t\\r\\n\\v\\f',
        },
        {
            class: '[[:upper:]]',
            equivalent: 'A-Z',
        },
        {
            class: '[[:word:]]',
            equivalent: 'A-Za-z0-9_',
        },
        {
            class: '[[:xdigit:]]',
            equivalent: 'A-Fa-f0-9',
        },
    ];

    /**
     * Searches for the next given flag (bit) in flagMask.
     *
     * @param startFlag Last flag checked, so the flag from which to start. Note: the flag will be left shifted at once.
     *          So if you pass value 4, value 8 will be the first value checked.
     * @param flagMask The entire mask consisting of 1 and 0. Something like 1011001 (which is 89 according to the windows calculator. Hm... - might be right, though.).
     */
    private static nextFlag(startFlag: number, flagMask: number): number {
        let n = startFlag;

        while ((n <<= 1) < Validator.maxFlag) {
            if ((n & flagMask) === n) {
                return n;
            }
        }

        return -1;
    }

    private static isRequired(field: ValidatorInput): boolean {
        return (field.checkMask & ValidationChecks.NOT_EMPTY_CHECK) === ValidationChecks.NOT_EMPTY_CHECK;
    }

    public static isEmpty(field: ValidatorInput): boolean {
        return !field.value || field.value.length === 0;
    }

    public static isCustomRegexOk(field: ValidatorInput): boolean {
        // Hier muessen die POSIX-Werte, z.B. [:punct:] uebersetzt werden, da diese in JavaScript nicht moeglich sind
        var parsedRegEx: RegExp = this.replacePOSIX(field.regex);

        return parsedRegEx.test(field.value);
    }

    private static replacePOSIX(regex: RegExp): RegExp {
        var regExString = regex.source;
        for (const posix of this.POSIX) {
            regExString = regExString.replace(posix.class, posix.equivalent);
        }

        return new RegExp(regExString, regex.flags);
    }

    private static isDateRegexOk(field: ValidatorInput): boolean {
        return Validator.DATE_REGEX.test(field.value);
    }

    private static isOptionSelected(field: ValidatorInput): boolean {
        return !Validator.isEmpty(field);
    }

    private static isValidByFunction(field: ValidatorInput, errorsRef: ValidationError[]): void {
        field.checkFunction(field);

        if (!field.valid) {
            let errorMsg = `Wert für '${field.label}' fehlt oder ist inkorrekt.`

            if (field.useThisErrorMsg && field.errorMsg) {
                errorMsg = field.errorMsg;
            }

            errorsRef.push(new ValidationError(field.label, errorMsg));
        }
    }

    private static isValueTrue(field: ValidatorInput): boolean {
        return field.value === true;
    }

    static isDateCorrect(field: ValidatorInput): boolean {
        if (!Validator.DATE_REGEX.test(field.value)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_WRONG_DATE_FORMAT;
            return false;
        }

        return true;
    }

    static isDateInFuture(field: ValidatorInput): boolean {
        const givenDate: Date = new Date().fromGermanString(field.value);
        console.debug('givenDate', givenDate);

        return Validator.today < givenDate;
    }

    static isDateInRange(field: ValidatorInput): boolean {
        const givenDate: Date = new Date().fromGermanString(field.value);
        const today: Date = new Date();
        const latestDate: Date = new Date(today.getUTCFullYear() + 10, today.getUTCMonth(), today.getUTCDate());

        return latestDate > givenDate;
    }

    static isPercent(field: ValidatorInput): boolean {
        const dec: string = field.value.replace(',', '.');
        const num: number = parseFloat(dec);
        if (isNaN(num)) {
            return false;
        }

        return num >= 1 && num <= 100;
    }

    /* END util methods. */

    /**
     * This method works recursively! Just pass 1 as currFlag and the method will ask for next flags every time
     * it calls itself. It calls itself if a check passed. Then a next error is being searched.
     *
     * @param field The field descriptor with value inside. We will modify this field, if a check failed.
     * @param currFlag Should be 1, if you call it. 0 is forbidden, because we can't shift 0.
     * @param errorsRef Reference of error collection to pass.
     */
    static doValidate = (field: ValidatorInput, currFlag: number, errorsRef: ValidationError[]): boolean => {
        const nFlag = Validator.nextFlag(currFlag, field.checkMask); // First get the next issue (flag) to check.
        switch (nFlag) {
            case ValidationChecks.NOT_EMPTY_CHECK:
                if (Validator.isEmpty(field)) {
                    if (!field.useThisErrorMsg) {
                        field.errorMsg = Validator.MSG_ON_REQUIRED_BUT_EMPTY.replace('$_label', field.label);
                    }
                    errorsRef.push(new ValidationError(field.label, `Wert für '${field.label}' fehlt.`));
                    return false;
                }
                return Validator.doValidate(field, nFlag, errorsRef);
            case ValidationChecks.SELECT_BOX_SELECTED_CHECK:
                if (!Validator.isOptionSelected(field)) {
                    if (!field.useThisErrorMsg) {
                        field.errorMsg = Validator.MSG_ON_NONE_SELECTED.replace('$_label', field.label);
                    }
                    errorsRef.push(new ValidationError(
                        field.label,
                        `Wert für '${field.label}' nicht ausgewählt.`,
                    ));
                    return false;
                }
                return true;
            case ValidationChecks.CUSTOM_REGEX_CHECK:
                if (Validator.isEmpty(field)) {
                    /*
                     * We already checked, if it is empty, but only if it is required. If not required,
                     * empty is ok.
                     */
                    return true;
                }
                if (!Validator.isCustomRegexOk(field)) {
                    if (!field.useThisErrorMsg) {
                        field.errorMsg = Validator.MSG_ON_REGEX_FAIL.replace('$_label', field.label).replace(
                            '$_value',
                            field.value,
                        );
                    }
                    errorsRef.push(new ValidationError(
                        field.label,
                        `Wert für '${field.label}' ist nicht korrekt.`,
                    ));
                    return false;
                }
                return Validator.doValidate(field, nFlag, errorsRef);
            case ValidationChecks.DATE_REGEX_CHECK:
                if (Validator.isEmpty(field)) {
                    return true;
                }
                if (!Validator.isDateRegexOk(field)) {
                    if (!field.useThisErrorMsg) {
                        field.errorMsg = Validator.MSG_WRONG_DATE_FORMAT;
                    }
                    errorsRef.push(new ValidationError(
                        field.label,
                        `Datum '${field.label}' entspricht nicht dem Format 'TT.MM.JJJJ'.`,
                    ));
                    return false;
                }
                return Validator.doValidate(field, nFlag, errorsRef);
            case ValidationChecks.CHECK_BOX_SELECTED_CHECK:
                if (Validator.isEmpty(field)) {
                    return true;
                }
                if (!Validator.isValueTrue(field)) {
                    if (!field.useThisErrorMsg) {
                        field.errorMsg = '\'' + field.label + '\' ist nicht bestätigt.';
                    }
                    errorsRef.push(new ValidationError(field.label, `'${field.label}' ist nicht bestätigt.`));
                    return false;
                }
                return Validator.doValidate(field, nFlag, errorsRef);
            case ValidationChecks.CHECK_PERCENT:
                if (Validator.isEmpty(field)) {
                    return true;
                }
                if (!Validator.isPercent(field)) {
                    if (!field.useThisErrorMsg) {
                        field.errorMsg = Validator.MSG_WRONG_PERCENT;
                    }
                    errorsRef.push(new ValidationError(
                        field.label,
                        `Wert für '${field.label}' ist keine prozentuale Angabe.`,
                    ));
                    return false;
                }
                return Validator.doValidate(field, nFlag, errorsRef);
            default:
                if (field.checkFunction) {
                    Validator.isValidByFunction(field, errorsRef);
                    return field.valid;
                }
                return true;
        }
    };

}
